import { createColumnHelper } from "@tanstack/react-table";
import { type VariantProps } from "class-variance-authority";
import { type businessUnitUsersQuery } from "../api/queries";
import { UserActionMenu } from "../UserActionMenu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge, type badgeVariants } from "@/components/ui/badge";
import { type ResultOf } from "@/graphql";
import { toDisplayDate } from "@/lib/utils/dates";
import { formatName } from "@/lib/utils/formatting";
import { getUserInitials } from "@/settings/utils";

type BusinessUnitUser = NonNullable<ResultOf<typeof businessUnitUsersQuery>["businessUnitUsers"]>[0];
type User = NonNullable<BusinessUnitUser>;
type UserStatus = User["status"];
type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>["variant"]>;

const columnHelper = createColumnHelper<User>();

const getStatusBadgeVariant = (status: UserStatus): BadgeVariant => {
  switch (status) {
    case "ACTIVE":
      return "default";
    case "INACTIVE":
      return "secondary";
    case "INVITED":
      return "outline";
    default:
      return "secondary";
  }
};

const getStatusLabel = (status: UserStatus): string => {
  switch (status) {
    case "ACTIVE":
      return "Active";
    case "INACTIVE":
      return "Inactive";
    case "INVITED":
      return "Invited";
    default:
      return status;
  }
};

export const userTableColumns = (onUserUpdated?: () => void) => [
  columnHelper.accessor((row) => row, {
    id: "user",
    header: "User",
    size: 300,
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const nameA = `${rowA.original.user.firstName || ""} ${rowA.original.user.lastName || ""}`.toLowerCase();
      const nameB = `${rowB.original.user.firstName || ""} ${rowB.original.user.lastName || ""}`.toLowerCase();
      return nameA.localeCompare(nameB);
    },
    cell: ({ getValue }) => {
      const userRecord = getValue();
      return (
        <div className="flex items-center space-x-3">
          <Avatar>
            <AvatarImage
              src={userRecord.user.image || undefined}
              alt={`${userRecord.user.firstName} ${userRecord.user.lastName}`}
            />
            <AvatarFallback>{getUserInitials(userRecord.user.firstName, userRecord.user.lastName)}</AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium">
              {formatName({
                firstName: userRecord.user.firstName,
                lastName: userRecord.user.lastName,
              })}
            </div>
            <div className="text-sm text-muted-foreground">{userRecord.user.email}</div>
            {userRecord.user.jobTitle ? (
              <div className="text-xs text-muted-foreground">{userRecord.user.jobTitle}</div>
            ) : null}
          </div>
        </div>
      );
    },
  }),

  columnHelper.accessor("role.name", {
    id: "role",
    header: "Role",
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const roleA = rowA.original.role?.name || "";
      const roleB = rowB.original.role?.name || "";
      return roleA.localeCompare(roleB);
    },
    cell: ({ row }) => {
      const user = row.original;
      const roleName = user.role?.name || "No Role";
      return <Badge variant="secondary">{roleName}</Badge>;
    },
  }),

  columnHelper.accessor("status", {
    id: "status",
    header: "Status",
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const statusOrder = { ACTIVE: 0, INACTIVE: 1, INVITED: 2 };
      const statusA = statusOrder[rowA.original.status] ?? 3;
      const statusB = statusOrder[rowB.original.status] ?? 3;
      return statusA - statusB;
    },
    cell: ({ getValue }) => {
      const status = getValue();
      return <Badge variant={getStatusBadgeVariant(status)}>{getStatusLabel(status)}</Badge>;
    },
  }),

  columnHelper.accessor("user.lastLogin", {
    id: "lastLogin",
    header: "Last Login",
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const lastLoginA = rowA.original.user.lastLogin ? new Date(rowA.original.user.lastLogin) : new Date(0);
      const lastLoginB = rowB.original.user.lastLogin ? new Date(rowB.original.user.lastLogin) : new Date(0);
      return lastLoginA.getTime() - lastLoginB.getTime();
    },
    cell: ({ getValue }) => {
      const lastLogin = getValue();
      return lastLogin ? toDisplayDate(lastLogin, "MMM d, yyyy") : <span className="text-muted-foreground">Never</span>;
    },
  }),

  columnHelper.accessor("user.dateJoined", {
    id: "dateJoined",
    header: "Date Joined",
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const dateA = new Date(rowA.original.user.dateJoined);
      const dateB = new Date(rowB.original.user.dateJoined);
      return dateA.getTime() - dateB.getTime();
    },
    cell: ({ getValue }) => {
      const dateJoined = getValue();
      return toDisplayDate(dateJoined, "MMM d, yyyy");
    },
  }),

  columnHelper.accessor((row) => row, {
    id: "actions",
    header: "",
    size: 50,
    enableSorting: false,
    enableHiding: false,
    enableResizing: false,
    cell: ({ getValue }) => {
      const userRecord = getValue();
      return <UserActionMenu user={userRecord} onUserUpdated={onUserUpdated} />;
    },
  }),
];
